import { addHours } from "date-fns";
import subByTime from "date-fns/sub";
import { useEffect, useState } from "react";
import { useQuery } from "react-query";

import { getOperation } from "../../../api/merchant";
import { MerchantOperationsParamsFace, ValueOf } from "../../../api/types";
import { AssetMultiSelection } from "../../../components/AssetSelection";
import { ChainSingleSelection } from "../../../components/ChainSelection";
import { useDatePicker } from "../../../components/DatePicker";
import { useAssets, useChains } from "../../../components/FetchConfig";
import GeneralBtn from "../../../components/GeneralBtn";
import MpTextField from "../../../components/MpTextField";
import {
  Box,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
} from "../../../components/MuiGenerals";
import { useAlerting, usePermission, useTranslation } from "../../../hooks";
import { useAppSelector } from "../../../reducer/hooks";
import { selectProfile } from "../../../reducer/profileSlice";
import {
  batchIdUrlString,
  enumNewApprovalStatus,
  enumNewCreatorType,
  enumNewMerchantOperationStatus,
  EnumOperationCreatorFilter,
  enumOperationType,
  enumYesAndNo,
  FeatureCodes,
  tableConfig,
} from "../../../utils/constant";
import {
  downloadFiles,
  enumMapping,
  getFullApiResponse,
  separateBatchStrings,
  sortAlphabetically,
  toDisplayTime,
} from "../../../utils/helper";
import { genField } from "../../../utils/HelperComp";
import { customSx } from "../../../utils/styling";
import { useZusDialogStore, useZusTabsStore } from "../../../zustand/store";
import StatusButtonGroup from "./StatusButtonGroup";
import CurrencyPairSingleSelection from "../Swap/components/CurrencyPairSingleSelection";
import FromToCurrencyDirection from "../Swap/Content/components/FromToCurrencyDirection";
import { useCurrencyDirection } from "../Swap/hooks";
import { getTokensFromCurrencyPair } from "../Swap/helpers/getTokensFromCurrencyPair";
import { SingleSelection } from "src/components/GeneralSelection";
import MultipleSelection from "src/components/MultipleSelection,";
import { IoperationTab } from "./types/IoperationTab";

const Pkey = FeatureCodes.assetManagement.MerchantOperation;
const Enum = {
  approvalStatus: enumMapping(enumNewApprovalStatus),
};

export const operationTypeEnum: Record<
  IoperationTab,
  ValueOf<typeof enumOperationType> | ValueOf<typeof enumOperationType>[] | ""
> = {
  all: "",
  api_withdraw_tab: enumOperationType.Withdraw,
  portal_withdraw_tab: enumOperationType.Withdraw,
  sweep_tab: [enumOperationType.Sweep, enumOperationType.ApproveAllowance],
  swap_tab: enumOperationType.Uniswap,
  contract_sweep: enumOperationType.ContractSweep,
};
export const operationCreatorFilterEnum: Record<
  IoperationTab,
  EnumOperationCreatorFilter
> = {
  all: EnumOperationCreatorFilter.All,
  api_withdraw_tab: EnumOperationCreatorFilter.ApiWithdraw,
  portal_withdraw_tab: EnumOperationCreatorFilter.BatchWithdraw,
  sweep_tab: EnumOperationCreatorFilter.Sweep,
  swap_tab: EnumOperationCreatorFilter.Swap,
  contract_sweep: EnumOperationCreatorFilter.ApproveAllowance,
};

const creatorTypeEnum: Record<IoperationTab, string[]> = {
  all: [],
  api_withdraw_tab: [enumNewCreatorType.CreatorApi],
  portal_withdraw_tab: [enumNewCreatorType.CreatorPortal],
  sweep_tab: [],
  swap_tab: [],
  contract_sweep: [],
};

export default function FilterBar(props: any) {
  const { t, tc, translate } = useTranslation("assetManagement");
  const te = (key: string) => translate(`enumConstants.${key}`);
  const { alerting } = useAlerting();
  const { hasPermission } = usePermission();
  const { selectChainByPieces } = useChains();
  const { selectAssetNamesByChain } = useAssets();
  const profile = useAppSelector(selectProfile);

  const { setList, setPage, page, refreshBatchTable, count, listMapping } =
    props;
  const operationTab = props.operationTab as IoperationTab;
  const zusTabsStore = useZusTabsStore((state) => state);

  const operationType = operationTypeEnum[operationTab];

  function getNow() {
    const timezone = profile.timezone;
    const defaultTimezone = (new Date().getTimezoneOffset() * -1) / 60;
    const totalDiff = Number(timezone) - defaultTimezone;
    return addHours(new Date(), totalDiff);
  }

  const now = getNow();

  const oneMonthAgo = new Date(
    subByTime(now, { months: 1 }).toDateString() + ", 00:00:00",
  );
  const endOfToday = new Date(now.toDateString() + ", 23:59:59");
  const defaultCreatedDates = {
    start: { defaultDate: oneMonthAgo },
    end: { defaultDate: endOfToday },
  };
  const DateObj = {
    Created: useDatePicker(defaultCreatedDates),
    Modified: useDatePicker(),
  };
  const rawSearchParams = {
    batchId: [],
    chain_name: "",
    asset_names: [],
    operation_type: operationType as string[],
    operation_creator_filter: 0,
    status: zusTabsStore.gridTabsEumn === 1 ? ["4"] : [],
    callback_status: [],
    merchant_id: "",
    creator_type: creatorTypeEnum[operationTab],
    approval_completed: "",
    multisig: "",
    created_date_from: DateObj.Created.start || undefined,
    created_date_to: DateObj.Created.end || undefined,
    last_modified_date_from: DateObj.Modified.start || undefined,
    last_modified_date_to: DateObj.Modified.end || undefined,
    currencyPair: "",
    fromCurrency: "",
    toCurrency: "",
  };

  const urlBatchId: any = window.location.search.split(batchIdUrlString).pop();
  const initSearchParams = urlBatchId
    ? { ...rawSearchParams, batchId: [urlBatchId] }
    : { ...rawSearchParams };
  const [fields, setFields] =
    useState<MerchantOperationsParamsFace>(initSearchParams);
  const [apiParams, setApiParams] =
    useState<MerchantOperationsParamsFace>(initSearchParams);

  useEffect(() => {
    let theStatus = [] as string[];
    if (zusTabsStore.gridTabsEumn == 1) {
      setFields((fields) => ({
        ...fields,
        status: [
          enumNewMerchantOperationStatus.Prepared,
          enumNewMerchantOperationStatus.Verified,
          enumNewMerchantOperationStatus.Multisig,
          enumNewMerchantOperationStatus.ManualVerification,
        ],
      }));
      theStatus = [
        enumNewMerchantOperationStatus.Prepared,
        enumNewMerchantOperationStatus.Verified,
        enumNewMerchantOperationStatus.Multisig,
        enumNewMerchantOperationStatus.ManualVerification,
      ];
    } else if (zusTabsStore.gridTabsEumn == 2) {
      setFields((fields) => ({
        ...fields,
        status: ["-3", "-2", "-1", "4"],
      }));
      theStatus = ["-3", "-2", "-1", "4"];
    } else if (zusTabsStore.gridTabsEumn == 0) {
      setFields((fields) => ({ ...fields, status: [] }));
      theStatus = [];
    }
    DateObj.Created.resetDate();
    const timer = setTimeout(() => {
      //runs search() with latest state and params
      const dom = document.querySelector('[label="search"]') as HTMLElement;
      dom.click();
    }, 100);
    setPage(0);
    return () => {
      clearTimeout(timer);
      setFields((fields) => ({ ...fields, status: [] }));
    };
  }, [zusTabsStore.gridTabsEumn]);
  const allAssetNames = selectAssetNamesByChain({
    chain_name: fields.chain_name,
  });

  const setAssets = (asset_names: string[]) =>
    setFields((fields) => ({ ...fields, asset_names }));
  const getParams = () => {
    const {
      chain_name,
      batchId,
      merchant_id,
      operation_type,
      multisig,
      ...rest
    } = apiParams;
    const merchant_ids = separateBatchStrings(merchant_id);
    const { chain_id, chain_type } = selectChainByPieces({ chain_name });

    const getIsMultisign = () => {
      switch (multisig) {
        case enumYesAndNo.yes:
          return true;
        case enumYesAndNo.no:
          return false;
        default:
          return undefined;
      }
    };

    const is_multisign = getIsMultisign();

    return {
      ...rest,
      batchIdArr: batchId,
      merchant_order_ids: merchant_ids,
      chain_id,
      chain_type,
      page,
      pageSize: tableConfig.pageSize,
      operation_type: [...operation_type],
      is_multisign,
    };
  };
  const zusDialog = useZusDialogStore();
  const params = getParams();
  const theFetchApi = getOperation(operationTab);
  const apiRes = useQuery(
    ["getOperation", params, refreshBatchTable],
    () => {
      zusDialog.openExtra("loadingDialog");
      return theFetchApi(params);
    },
    {
      onSettled: () => zusDialog.closeExtra(),
      onSuccess: (data) => {
        if (!data) return;
        setList(data);
      },
    },
  );

  const refetch = () => setTimeout(apiRes.refetch, 0);

  useEffect(() => {
    setAssets([]);
  }, [fields.chain_name]);

  const { handleSwitchCurrency } = useCurrencyDirection({
    currencyPair: fields.currencyPair,
    onCurrencyChange: (fromCurrency: string, toCurrency: string) => {
      setFields({
        ...fields,
        fromCurrency,
        toCurrency,
      });
    },
  });

  const onSearch = async () => {
    setPage(0);
    setApiParams({
      ...fields,
      created_date_from: DateObj.Created.start || undefined,
      created_date_to: DateObj.Created.end || undefined,
      last_modified_date_from: DateObj.Modified.start || undefined,
      last_modified_date_to: DateObj.Modified.end || undefined,
    });
    refetch();
  };

  const onReset = () => {
    setFields({ ...rawSearchParams, status: [] });
    DateObj.Created.clearDate();
    DateObj.Modified.clearDate();
  };

  const onExport = async () => {
    if (count === 0) {
      return alerting("error", tc("no_data_export"));
    }
    const apiFn = (page: number, pageSize: number, signal: any) =>
      theFetchApi({ ...params, page, pageSize }, { signal });
    const rawRes = await getFullApiResponse(apiFn, count);
    if (rawRes.length === 0) return;
    const transformApprovals = ({
      transaction_approval_details,
      ...item
    }: any) => {
      const approvals = [0, 1, 2, 3].map((i) => {
        // const details = transaction_approval_details[i] || {};
        const details =
          transaction_approval_details.find((d: any) => d?.seq === i + 1) || {};
        const position = t("position" + (i + 1));
        return {
          [`${position} ${t("operated_by")}`]: details.operated_by || "",
          [`${position} ${t("approval_result")}`]:
            Enum.approvalStatus[details.approval_result] || "",
          [`${position} ${t("operated_date")}`]: toDisplayTime(
            details.operated_date,
          ),
        };
      });
      return { ...item, ...Object.assign({}, ...approvals) };
    };
    const omitKeys = ["rawData", "chain_id", "chain_type"];
    const res = listMapping("name", rawRes, {
      translate,
      omitKeys,
      operationTab,
    }).map(transformApprovals);
    const config = {};
    downloadFiles(`Merchant Operation`, res, config);
  };

  const onFilter =
    (type: string) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      let { value }: any = e.target;
      const newValue =
        value == ""
          ? value
          : typeof value === "string"
          ? value.split(",")
          : value;
      setFields((fields) => ({ ...fields, [type]: newValue }));
    };

  const onArraySelectChange = (e: SelectChangeEvent<string[]>) => {
    const { name, value } = e.target;
    const newValue = typeof value === "string" ? value.split(",") : value;

    return setFields((fields) => ({ ...fields, [name]: newValue }));
  };

  const onChange =
    (type: string) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const value = e.target.value;
      setFields((fields) => ({ ...fields, [type]: value }));
    };
  const onFilterChange =
    (type: string) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const value = e.target.value;
      setFields((fields) => ({ ...fields, [type]: value }));
    };

  const F = genField({ t }, [
    ["created_date", <DateObj.Created.Picker type="dateTime" />],
    [
      "chain_name",
      <ChainSingleSelection
        label={t("phChain_name")}
        setChoice={(chain_name) => setFields((f) => ({ ...f, chain_name }))}
        choice={fields.chain_name}
      />,
    ],
    [
      "asset_name",
      <AssetMultiSelection
        label={t("phAsset_name")}
        setChoices={setAssets}
        choices={fields.asset_names}
        allItems={allAssetNames}
      />,
    ],
    [
      "operation_type",
      <MultipleSelection
        label={t("phOperation_type")}
        value={fields.operation_type || []}
        onChange={(arr) => setFields((f) => ({ ...f, operation_type: arr }))}
        isNoSorting
        enumData={enumOperationType}
      />,
    ],
    [
      "status",
      <FormControl>
        <InputLabel>{t("phStatus")}</InputLabel>
        <Select
          name="status"
          multiple
          value={fields.status}
          onChange={onArraySelectChange}
        >
          {sortAlphabetically(
            Object.entries(enumNewMerchantOperationStatus),
          ).map(([name, id]: string[], i) => (
            <MenuItem key={i} value={id}>
              {te(name)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>,
    ],
    [
      "currency_pair",
      <CurrencyPairSingleSelection
        label={t("phCurrencyPair")}
        value={fields.currencyPair}
        chainName={fields.chain_name}
        onChange={(e) => {
          const currencyPairArr = getTokensFromCurrencyPair(e.target.value);
          setFields({
            ...fields,
            currencyPair: e.target.value,
            fromCurrency: currencyPairArr[0],
            toCurrency: currencyPairArr[1],
          });
        }}
        onClear={() => {}}
      />,
    ],
    [
      "direction",
      <div style={{ paddingTop: "6px", paddingLeft: "12px" }}>
        <FromToCurrencyDirection
          fromCurrency={fields.fromCurrency}
          toCurrency={fields.toCurrency}
          onSwitch={() => {
            handleSwitchCurrency();
          }}
        />
      </div>,
    ],
    [
      "batch_id",
      <MpTextField
        fullWidth={true}
        name="batchId"
        label={t("phBatch_id")}
        value={fields.batchId}
        onChange={onFilter("batchId")}
      />,
    ],
    [
      "approval_completed",
      <MpTextField
        fullWidth={true}
        name="approval_completed"
        label={t("ph_approval_completed")}
        value={fields.approval_completed}
        onChange={onChange("approval_completed")}
      />,
    ],
    [
      "merchant_order_id",
      <MpTextField
        name="merchant_id"
        label={t("phMerchant_id")}
        value={fields.merchant_id}
        onChange={onFilterChange("merchant_id")}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              {params.merchant_order_ids.length}
            </InputAdornment>
          ),
        }}
      />,
    ],
    [
      "creator_type",
      <FormControl>
        <InputLabel>{t("phCreator_type")}</InputLabel>
        <Select
          name="creator_type"
          multiple
          value={fields.creator_type}
          onChange={onArraySelectChange}
        >
          {sortAlphabetically(Object.entries(enumNewCreatorType)).map(
            ([name, id]: any, i) => (
              <MenuItem key={i} value={id}>
                {te(name)}
              </MenuItem>
            ),
          )}
        </Select>
      </FormControl>,
    ],
    [
      "multisig",
      <SingleSelection
        label={tc("phSelection", { fieldName: t("multisig") })}
        value={fields.multisig}
        onChange={(e) => {
          setFields((f) => ({ ...f, multisig: e.target.value }));
        }}
        enumData={enumYesAndNo}
        clearSelect={() => setFields((f) => ({ ...f, multisig: "" }))}
      />,
    ],
    ["last_modified_time", <DateObj.Modified.Picker type="dateTime" />],
  ]);
  const showExportButton =
    (operationTab === "all" && hasPermission(Pkey.TabAll.Export)) ||
    (operationTab === "api_withdraw_tab" &&
      hasPermission(Pkey.TabApiWithdraw.Export)) ||
    (operationTab === "portal_withdraw_tab" &&
      hasPermission(Pkey.TabPortalWithdraw.Export)) ||
    (operationTab === "sweep_tab" && hasPermission(Pkey.TabSweep.Export)) ||
    (operationTab === "swap_tab" && hasPermission(Pkey.TabSwap.Export));
  return (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          gap: "1rem",
          marginBottom: "1rem",
          "& .MuiButton-root": {
            color: "black",
            borderColor: "#AAAAAA",
          },
        }}
      >
        <div>
          <StatusButtonGroup />
        </div>
        <div>
          <GeneralBtn
            label="export"
            isHidden={!showExportButton}
            onClick={onExport}
          />
        </div>
      </Box>
      <Box sx={customSx.gridFilter} className="gridFilter">
        {F.created_date}
        {F.last_modified_time}
        {F.chain_name}
        {operationTab === "swap_tab" && F.currency_pair}
        {operationTab !== "swap_tab" && F.asset_name}
        {F.status}
        {operationTab === "swap_tab" && F.direction}
        {F.multisig}
        {F.batch_id}
        {F.merchant_order_id}
        {operationTab === "all" && F.operation_type}
        {operationTab == "sweep_tab"
          ? F.creator_type
          : operationTab == "all"
          ? F.creator_type
          : ""}
        {F.approval_completed}
      </Box>
      <Box sx={customSx.filterB}>
        <GeneralBtn label="search" onClick={onSearch} />
        <GeneralBtn label="reset" onClick={onReset} />
      </Box>
    </>
  );
}
